(() => {
  'use strict';

  const script = document.currentScript;
  const root = new URL(script.dataset.root || '/', location.origin);
  const searchPath = new URL(script.dataset.search || '/search', location.origin).pathname;
  const cacheKey = `dezin-search-v2:${script.src}:${root.pathname}:${document.documentElement.lang}`;
  const scopedPattern = /^\(title:[\p{L}\p{N}]+ OR tag:[\p{L}\p{N}]+(?: OR (?:title|tag|body):[\p{L}\p{N}]+)*\)(?: AND \(title:[\p{L}\p{N}]+ OR tag:[\p{L}\p{N}]+(?: OR (?:title|tag|body):[\p{L}\p{N}]+)*\))*$/u;
  const words = (text) => String(text).normalize('NFKC').toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) || [];
  const isScoped = (text) => scopedPattern.test(text || '');
  let vocabularyPromise;
  let navigationId = 0;

  async function loadVocabulary() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey));
      if (cached && Date.now() - cached.time < 300000 && Array.isArray(cached.words)) {
        return new Set(cached.words);
      }
    } catch (error) {}

    const vocabulary = new Set();
    const visited = new Set();
    let url = new URL(root);
    url.searchParams.set('section_id', 'dezin-search-vocabulary');
    while (url) {
      if (url.origin !== location.origin || visited.has(url.href)) throw new Error('Invalid search vocabulary page');
      visited.add(url.href);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      let html;
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Search vocabulary: ${response.status}`);
        html = await response.text();
      } finally {
        clearTimeout(timeout);
      }
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const payload = documentFragment.querySelector('[data-dezin-search-vocabulary]');
      if (!payload) throw new Error('Search vocabulary unavailable');
      const page = JSON.parse(payload.textContent);
      if (!Array.isArray(page.entries)) throw new Error('Invalid search vocabulary');
      page.entries.forEach((entry) => words(entry).forEach((word) => vocabulary.add(word)));
      url = page.next ? new URL(page.next, root) : null;
      if (url) url.searchParams.set('section_id', 'dezin-search-vocabulary');
    }
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ time: Date.now(), words: [...vocabulary] }));
    } catch (error) {}
    return vocabulary;
  }

  function getVocabulary() {
    if (!vocabularyPromise) {
      vocabularyPromise = loadVocabulary().catch((error) => {
        vocabularyPromise = null;
        throw error;
      });
    }
    return vocabularyPromise;
  }

  async function query(term) {
    if (isScoped(term)) return term;
    const tokens = [...new Set(words(term))];
    if (!tokens.length) return '(title:dezinsearchnomatch OR tag:dezinsearchnomatch)';
    let vocabulary;
    try {
      vocabulary = await getVocabulary();
    } catch (error) {
      vocabulary = new Set();
    }
    return tokens.map((token) => {
      const matches = [...vocabulary].filter((word) => word.startsWith(token));
      if (!matches.length) matches.push(token);
      matches.sort((a, b) => (a === token ? -1 : b === token ? 1 : a.localeCompare(b)));
      return `(${matches.map((word) => `title:${word} OR tag:${word} OR body:${word}`).join(' OR ')})`;
    }).join(' AND ');
  }

  function displayTerm(url = new URL(location.href)) {
    const term = url.searchParams.get('q') || '';
    if (!isScoped(term)) return term;
    return url.searchParams.get('oq') || term.split(' AND ').map((group) => group.match(/title:([\p{L}\p{N}]+)/u)[1]).join(' ');
  }

  async function prepareUrl(value, term = displayTerm(new URL(value, location.origin))) {
    const url = new URL(value, location.origin);
    url.searchParams.set('q', await query(term));
    url.searchParams.set('oq', term.trim());
    url.searchParams.set('type', 'product');
    url.searchParams.set('ds', '2');
    url.searchParams.set('options[unavailable_products]', 'show');
    return url;
  }

  function restoreDisplay() {
    if (location.pathname !== searchPath) return;
    const url = new URL(location.href);
    const scoped = url.searchParams.get('q');
    if (!isScoped(scoped)) return;
    const term = displayTerm(url);
    const renderedTerm = scoped.split(' AND ').map((group) => group.match(/title:([\p{L}\p{N}]+)/u)[1]).join(' ');
    document.querySelectorAll('form input[name="q"]').forEach((input) => {
      if (input.type !== 'hidden' && (isScoped(input.value) || input.value === renderedTerm)) input.value = term;
    });
    document.querySelectorAll('[data-dezin-search-text]').forEach((element) => {
      const text = element.textContent.split(renderedTerm).join(term);
      if (element.textContent !== text) element.textContent = text;
    });
    document.title = document.title.split(scoped).join(term);
  }

  async function navigate(url, term, replace = false) {
    const id = ++navigationId;
    const destination = await prepareUrl(url, term);
    if (id !== navigationId) return;
    if (replace && destination.href === location.href) return;
    if (replace) location.replace(destination.href);
    else location.assign(destination.href);
  }

  window.DezinSearch = { query, prepareUrl, displayTerm, isScoped, restoreDisplay };

  document.addEventListener('focusin', (event) => {
    if (event.target.matches('input[name="q"]')) getVocabulary().catch(() => {});
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const url = new URL(form.action, location.origin);
    if (url.origin !== location.origin || url.pathname !== searchPath) return;
    const input = form.querySelector('input[name="q"]:not([type="hidden"])');
    if (!input) return;
    event.preventDefault();
    if (!input.value.trim()) return;
    const params = new URLSearchParams(new FormData(form));
    params.forEach((value, key) => url.searchParams.set(key, value));
    url.searchParams.delete('page');
    url.searchParams.delete('phcursor');
    navigate(url, input.value);
  }, true);

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || url.pathname !== searchPath || !url.searchParams.get('q') || (isScoped(url.searchParams.get('q')) && url.searchParams.get('ds') === '2')) return;
    event.preventDefault();
    navigate(url, displayTerm(url));
  }, true);

  restoreDisplay();
  const current = new URL(location.href);
  if (current.pathname === searchPath && current.searchParams.get('q') && !current.searchParams.has('section_id')) {
    navigate(current, displayTerm(current), true);
  }
  document.addEventListener('shopify:section:load', restoreDisplay);
})();

